import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  Tooltip,
} from "antd";
import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  LockOutlined,
  PlusOutlined,
  PoweroffOutlined,
  ReloadOutlined,
  SlidersOutlined,
  HistoryOutlined,
  TeamOutlined,
  WarningOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";
import { useBudget } from "../../../hooks/finance/useBudget";
import { usePayrollSetup } from "../../../hooks/finance/usePayrollSetup";
import PayrollComponentsTab from "../../../components/finance/payroll/PayrollComponentsTab";
import PayrollSalaryScalesTab from "../../../components/finance/payroll/PayrollSalaryScalesTab";
import PayrollStatutoryRulesTab from "../../../components/finance/payroll/PayrollStatutoryRulesTab";
import PayrollStructuresTab from "../../../components/finance/payroll/PayrollStructuresTab";
import PayrollEmployeeOverridesModal from "../../../components/finance/payroll/PayrollEmployeeOverridesModal";
import PayrollBanksTab from "../../../components/finance/payroll/PayrollBanksTab";
import PayrollSaccoTab from "../../../components/finance/payroll/PayrollSaccoTab";
import PayrollProfileBulkActions from "../../../components/finance/payroll/PayrollProfileBulkActions";
import { usePayrollSecurity } from "../../../hooks/finance/usePayrollSecurity";
import { payrollAmountInputProps } from "../../../components/finance/payroll/payrollAmountInput";
import { getPayrollComponentDefaults } from "../../../components/finance/payroll/payrollComponentDefaults";
import { getPayrollSchoolSectionOptions } from "../../../utils/payrollSchoolSections";

const { Text, Title } = Typography;

const dateValue = (value) => (value ? dayjs(value) : null);
const isoDate = (value) => (value ? value.format("YYYY-MM-DD") : null);

const sameValue = (left, right) => (left ?? null) === (right ?? null);

const bankDetailsChanged = (getFieldValue, selected) =>
  Boolean(
    selected &&
    (!sameValue(getFieldValue("bankId"), selected.bankId) ||
      !sameValue(getFieldValue("bankBranch"), selected.bankBranch) ||
      !sameValue(getFieldValue("bankBranchCode"), selected.bankBranchCode) ||
      !sameValue(getFieldValue("bankAccountName"), selected.bankAccountName) ||
      (getFieldValue("bankAccountNumber") || "").trim()),
  );

const lstDetailsChanged = (getFieldValue, selected) =>
  Boolean(
    selected &&
    (!sameValue(getFieldValue("lstEnabled"), selected.lstEnabled) ||
      !sameValue(getFieldValue("lstLocation"), selected.lstLocation)),
  );
const money = (value) => `UGX ${Number(value || 0).toLocaleString("en-UG")}`;
const CLIENT_ALLOWANCE_ORDER = [
  "CONS",
  "HOS",
  "HOD",
  "CTR",
  "UPPER",
  "LOWER",
  "HOUSING",
  "ARREARS",
  "TRANSPORT",
];
const EMPLOYEE_VALUE_KINDS = ["Earning", "Employee Deduction"];

const PayrollManagement = () => {
  const { payrollAccess } = usePayrollSecurity();
  const navigate = useNavigate();
  const { departments, fetchDepartments } = useBudget();
  const {
    profiles,
    components,
    structures,
    scales,
    statutoryRules,
    componentOverrides,
    banks,
    paymentAccounts,
    summary,
    loading,
    saving,
    refresh,
    saveProfile,
    deleteProfile,
    createEmployeeWithSalary,
    saveSalary,
    fetchSalaryAssignments,
    deleteSalary,
    reviseSalary,
    saveComponent,
    deleteComponent,
    saveStructure,
    deleteStructure,
    saveScale,
    deleteScale,
    fetchUgandaStatutoryDefaults,
    saveStatutoryRule,
    deleteStatutoryRule,
    fetchComponentOverrides,
    saveComponentOverride,
    deleteComponentOverride,
    copySetupVersion,
    runRuleAction,
    testDraftStatutoryRule,
    saveBank,
    deleteBank,
    savePaymentAccount,
    deletePaymentAccount,
    setDefaultPaymentAccount,
    fetchEmployeeBankHistory,
  } = usePayrollSetup();
  const [staff, setStaff] = useState([]);
  const [filters, setFilters] = useState({});
  const [profileOpen, setProfileOpen] = useState(false);
  const [salaryOpen, setSalaryOpen] = useState(false);
  const [salaryHistoryEmployee, setSalaryHistoryEmployee] = useState(null);
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [salaryHistoryLoading, setSalaryHistoryLoading] = useState(false);
  const [salaryCorrection, setSalaryCorrection] = useState(null);
  const [selected, setSelected] = useState(null);
  const [overrideEmployee, setOverrideEmployee] = useState(null);
  const [bankHistoryEmployee, setBankHistoryEmployee] = useState(null);
  const [bankHistory, setBankHistory] = useState([]);
  const [profileForm] = Form.useForm();
  const [salaryForm] = Form.useForm();
  const [salaryCorrectionForm] = Form.useForm();
  const taxExempt = Form.useWatch("taxExempt", profileForm);
  const nssfEnabled = Form.useWatch("nssfEnabled", profileForm);
  const lstEnabled = Form.useWatch("lstEnabled", profileForm);
  const selectedBankId = Form.useWatch("bankId", profileForm);
  const hireDate = Form.useWatch("hireDate", profileForm);
  const employeeCategory = Form.useWatch("employeeCategory", profileForm);
  const profileSchoolSection = Form.useWatch("schoolSection", profileForm);
  const initialScaleId = Form.useWatch("initialSalaryScaleId", profileForm);
  const initialStepId = Form.useWatch("initialSalaryScaleStepId", profileForm);
  const initialBasicSalary = Form.useWatch("initialBasicSalary", profileForm);
  const initialAllowances =
    Form.useWatch("initialAllowances", profileForm) || [];
  const salaryScaleId = Form.useWatch("salaryScaleId", salaryForm);
  const salaryScaleStepId = Form.useWatch("salaryScaleStepId", salaryForm);
  const salaryBasicSalary = Form.useWatch("basicSalary", salaryForm);
  const salaryCorrectionStart = Form.useWatch(
    "effectiveFrom",
    salaryCorrectionForm,
  );

  const effectiveToday = (item) =>
    (!item.effectiveFrom ||
      !dayjs(item.effectiveFrom).isAfter(dayjs(), "day")) &&
    (!item.effectiveTo || !dayjs(item.effectiveTo).isBefore(dayjs(), "day"));
  const activeStructuresFor = (category) =>
    structures.filter(
      (item) =>
        item.isActive &&
        effectiveToday(item) &&
        (!item.employeeCategory || item.employeeCategory === category),
    );
  const activeScalesFor = (category) =>
    scales.filter(
      (item) =>
        item.isActive &&
        effectiveToday(item) &&
        (!item.employeeCategory || item.employeeCategory === category),
    );
  const initialScale = scales.find((item) => item.scaleId === initialScaleId);
  const initialStep = initialScale?.steps?.find(
    (item) => item.stepId === initialStepId,
  );
  const initialSalaryOverride = Boolean(
    initialStep &&
    initialBasicSalary != null &&
    Number(initialBasicSalary) !== Number(initialStep.basicSalary),
  );
  const salaryScale = scales.find((item) => item.scaleId === salaryScaleId);
  const salaryStep = salaryScale?.steps?.find(
    (item) => item.stepId === salaryScaleStepId,
  );
  const salaryOverride = Boolean(
    salaryStep &&
    salaryBasicSalary != null &&
    Number(salaryBasicSalary) !== Number(salaryStep.basicSalary),
  );
  const selectedBankMaster = banks.find(
    (item) => item.bankId === selectedBankId,
  );
  const bankEffectiveFloor = selected?.bankEffectiveFrom
    ? dayjs(selected.bankEffectiveFrom)
    : null;
  const lstEffectiveFloor = selected?.lstEffectiveFrom
    ? dayjs(selected.lstEffectiveFrom)
    : null;
  const currentHistorySalary =
    salaryHistory.find((item) => item.isCurrent) || null;
  const employeeSetupComponents = useMemo(
    () =>
      components
        .filter(
          (item) =>
            item.isActive &&
            item.allowEmployeeOverride &&
            EMPLOYEE_VALUE_KINDS.includes(item.kind) &&
            effectiveToday(item),
        )
        .sort((left, right) => {
          const leftOrder = CLIENT_ALLOWANCE_ORDER.indexOf(left.code);
          const rightOrder = CLIENT_ALLOWANCE_ORDER.indexOf(right.code);
          return (
            (leftOrder === -1 ? CLIENT_ALLOWANCE_ORDER.length : leftOrder) -
            (rightOrder === -1 ? CLIENT_ALLOWANCE_ORDER.length : rightOrder)
          );
        }),
    [components],
  );

  const loadStaff = useCallback(async () => {
    const response = await Utilities.fetchWithAuth(
      "GET",
      domainSettings.urls.auth.listStaff,
      null,
      navigate,
    );
    if (response.ok)
      setStaff(
        Array.isArray(response.data)
          ? response.data
          : response.data?.results || [],
      );
  }, [navigate]);

  useEffect(() => {
    loadStaff();
    fetchDepartments();
  }, [fetchDepartments, loadStaff]);

  useEffect(() => {
    const timer = setTimeout(() => refresh(filters), filters.search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [filters, refresh]);

  const usedStaffIds = useMemo(
    () => new Set(profiles.map((item) => item.staffId)),
    [profiles],
  );

  const openProfile = async (profile = null) => {
    if (profile && payrollAccess.allowances_manage) {
      await fetchComponentOverrides(profile.profileId);
    }
    setSelected(profile);
    profileForm.resetFields();
    if (profile) {
      profileForm.setFieldsValue({
        ...profile,
        hireDate: dateValue(profile.hireDate),
        exitDate: dateValue(profile.exitDate),
        bankAccountNumber: "",
        bankEffectiveFrom: dateValue(profile.bankEffectiveFrom) || dayjs(),
        bankChangeReason: "",
        lstEffectiveFrom: dateValue(profile.lstEffectiveFrom) || dayjs(),
        lstChangeReason: "",
      });
    } else {
      profileForm.setFieldsValue({
        employeeCategory: "Teaching",
        employmentStatus: "Active",
        payrollEligible: true,
        residencyStatus: "Resident",
        taxExempt: false,
        nssfEnabled: true,
        lstEnabled: true,
        lstEffectiveFrom: dayjs(),
        bankEffectiveFrom: dayjs(),
        initialSalaryEffectiveFrom: dayjs(),
        initialAllowances: [],
      });
    }
    setProfileOpen(true);
  };

  const submitProfile = async () => {
    const values = await profileForm.validateFields();
    const profileValues = { ...values };
    [
      "initialSalaryStructureId",
      "initialSalaryScaleId",
      "initialSalaryScaleStepId",
      "initialBasicSalary",
      "initialSalaryEffectiveFrom",
      "initialSalaryOverrideReason",
      "initialAllowances",
    ].forEach((field) => delete profileValues[field]);
    const payload = {
      ...profileValues,
      hireDate: isoDate(values.hireDate),
      exitDate: isoDate(values.exitDate),
      taxExemptReason: values.taxExempt ? values.taxExemptReason : null,
      nssfNumber: values.nssfEnabled ? values.nssfNumber : null,
      lstLocation: values.lstEnabled ? values.lstLocation : null,
      bankEffectiveFrom: isoDate(values.bankEffectiveFrom),
      lstEffectiveFrom: isoDate(values.lstEffectiveFrom),
    };
    if (!payrollAccess.tax)
      [
        "residencyStatus",
        "tin",
        "taxExempt",
        "taxExemptReason",
        "lstLocation",
        "lstEnabled",
        "lstEffectiveFrom",
        "lstChangeReason",
      ].forEach((field) => delete payload[field]);
    if (!payrollAccess.nssf)
      ["nssfNumber", "nssfEnabled"].forEach((field) => delete payload[field]);
    if (!payrollAccess.bank)
      [
        "bankId",
        "bankName",
        "bankBranch",
        "bankBranchCode",
        "bankCode",
        "bankAccountName",
        "bankAccountNumber",
        "bankEffectiveFrom",
        "bankChangeReason",
      ].forEach((field) => delete payload[field]);
    if (!payrollAccess.sacco) delete payload.saccoAccountNo;
    const result = selected
      ? await saveProfile(selected.profileId, payload)
      : payrollAccess.salary_full
        ? await createEmployeeWithSalary(
            payload,
            {
              salaryStructureId: values.initialSalaryStructureId || null,
              salaryScaleStepId: values.initialSalaryScaleStepId || null,
              basicSalary: String(values.initialBasicSalary),
              effectiveFrom: isoDate(values.initialSalaryEffectiveFrom),
              paymentFrequency: "Monthly",
              salaryOverrideReason: values.initialSalaryOverrideReason || null,
            },
            (values.initialAllowances || []).map((item) => {
              const component = employeeSetupComponents.find(
                (candidate) => candidate.componentId === item.componentId,
              );
              const usePercentage =
                component?.calculationMethod?.startsWith("Percentage");
              return {
                componentId: item.componentId,
                amount: usePercentage ? null : String(item.amount),
                percentage: usePercentage ? String(item.percentage) : null,
                effectiveFrom: isoDate(values.initialSalaryEffectiveFrom),
                effectiveTo: isoDate(item.effectiveTo),
                reason: "Initial employee setup from appointment letter.",
                isActive: true,
              };
            }),
          )
        : await saveProfile(null, payload);
    if (result) {
      setProfileOpen(false);
      refresh(filters);
    }
  };

  const openSalary = (profile) => {
    setSelected(profile);
    salaryForm.resetFields();
    salaryForm.setFieldsValue({
      basicSalary: profile.currentSalary
        ? Number(profile.currentSalary.basicSalary)
        : null,
      salaryStructureId: profile.currentSalary?.salaryStructureId || null,
      salaryScaleId: profile.currentSalary?.salaryScaleId || null,
      salaryScaleStepId: profile.currentSalary?.salaryScaleStepId || null,
      salaryOverrideReason: profile.currentSalary?.salaryOverrideReason || null,
      effectiveFrom: profile.currentSalary
        ? dayjs().isAfter(dayjs(profile.currentSalary.effectiveFrom), "day")
          ? dayjs()
          : dayjs(profile.currentSalary.effectiveFrom).add(1, "day")
        : dayjs(),
      paymentFrequency: "Monthly",
      revisionReason: "",
      notes: "",
    });
    setSalaryOpen(true);
  };

  const submitSalary = async () => {
    const values = await salaryForm.validateFields();
    const payload = {
      profileId: selected.profileId,
      salaryStructureId: values.salaryStructureId || null,
      salaryScaleStepId: values.salaryScaleStepId || null,
      basicSalary: String(values.basicSalary),
      salaryOverrideReason: values.salaryOverrideReason || null,
      revisionReason: values.revisionReason || null,
      effectiveFrom: isoDate(values.effectiveFrom),
      paymentFrequency: "Monthly",
      notes: values.notes || "",
    };
    const result = selected.currentSalary
      ? await reviseSalary(selected.currentSalary.assignmentId, payload)
      : await saveSalary(null, payload);
    if (result) {
      setSalaryOpen(false);
      if (salaryHistoryEmployee) {
        setSalaryHistory(
          await fetchSalaryAssignments(salaryHistoryEmployee.profileId),
        );
      }
      refresh(filters);
    }
  };

  const openSalaryHistory = async (profile) => {
    setSalaryHistoryEmployee(profile);
    setSalaryHistoryLoading(true);
    try {
      setSalaryHistory(await fetchSalaryAssignments(profile.profileId));
    } finally {
      setSalaryHistoryLoading(false);
    }
  };

  const openSalaryCorrection = (record) => {
    setSalaryCorrection(record);
    salaryCorrectionForm.resetFields();
    salaryCorrectionForm.setFieldsValue({
      effectiveFrom: dateValue(record.effectiveFrom),
      effectiveTo: dateValue(record.effectiveTo),
      correctionReason: "",
    });
  };

  const submitSalaryCorrection = async () => {
    const values = await salaryCorrectionForm.validateFields();
    const result = await saveSalary(salaryCorrection.assignmentId, {
      effectiveFrom: isoDate(values.effectiveFrom),
      effectiveTo: isoDate(values.effectiveTo),
      revisionReason: values.correctionReason,
    });
    if (result) {
      setSalaryCorrection(null);
      setSalaryHistory(
        await fetchSalaryAssignments(salaryHistoryEmployee.profileId),
      );
      refresh(filters);
    }
  };

  const removeSalaryRecord = async (record) => {
    if (!(await deleteSalary(record.assignmentId))) return;
    setSalaryHistory(
      await fetchSalaryAssignments(salaryHistoryEmployee.profileId),
    );
    refresh(filters);
  };

  const columns = [
    {
      title: "Employee",
      key: "employee",
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.staffName || row.staffEmail}</Text>
          <Text type="secondary">
            {row.staffCode || "No staff ID"} · {row.designation}
          </Text>
        </Space>
      ),
    },
    {
      title: "Work details",
      key: "work",
      responsive: ["md"],
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text>{row.departmentName || "No department"}</Text>
          <Text type="secondary">
            {row.schoolSection || "No section"} ·{" "}
            {row.costCentre || "No cost centre"}
          </Text>
          <Text type="secondary">
            {row.employeeCategory} · {row.employmentStatus}
          </Text>
        </Space>
      ),
    },
    payrollAccess.salary_full && {
      title: "Current basic salary",
      key: "salary",
      render: (_, row) =>
        row.currentSalary ? (
          <Space direction="vertical" size={0}>
            <Text strong>{money(row.currentSalary.basicSalary)}</Text>
            <Text type="secondary">
              {row.currentSalary.salaryStructureName || "No salary structure"}
            </Text>
            <Text type="secondary">
              {row.currentSalary.salaryScaleName
                ? `${row.currentSalary.salaryScaleName} · ${row.currentSalary.salaryScaleStepName}`
                : "No salary scale"}
            </Text>
            <Text type="secondary">
              From{" "}
              {dayjs(row.currentSalary.effectiveFrom).format("DD MMM YYYY")}
            </Text>
          </Space>
        ) : (
          <Tag color="orange">Not set</Tag>
        ),
    },
    {
      title: "Readiness",
      key: "readiness",
      render: (_, row) =>
        row.isReady ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            Ready
          </Tag>
        ) : (
          <Space direction="vertical" size={2}>
            <Tag color="warning" icon={<WarningOutlined />}>
              Incomplete
            </Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.readinessIssues?.join(", ") || "Not payroll eligible"}
            </Text>
          </Space>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      align: "center",
      render: (_, row) => (
        <Space size="small">
          <Tooltip title="Edit payroll profile">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openProfile(row)}
            />
          </Tooltip>
          {payrollAccess.salary_full && (
            <Tooltip title="Salary history and revisions">
              <Button
                type="text"
                size="small"
                icon={<WalletOutlined />}
                onClick={() => openSalaryHistory(row)}
              />
            </Tooltip>
          )}
          {payrollAccess.allowances_manage && (
            <Tooltip title="Employee earnings & deductions">
              <Button
                type="text"
                size="small"
                icon={<SlidersOutlined />}
                onClick={async () => {
                  setOverrideEmployee(row);
                  await fetchComponentOverrides(row.profileId);
                }}
              />
            </Tooltip>
          )}
          {payrollAccess.bank && (
            <Tooltip title="Masked bank-detail history">
              <Button
                type="text"
                size="small"
                icon={<HistoryOutlined />}
                onClick={async () => {
                  setBankHistoryEmployee(row);
                  setBankHistory(await fetchEmployeeBankHistory(row.profileId));
                }}
              />
            </Tooltip>
          )}
          <Tooltip
            title={
              row.payrollEligible
                ? "Exclude from payroll"
                : "Include in payroll"
            }
          >
            <Button
              type="text"
              size="small"
              danger={row.payrollEligible}
              style={row.payrollEligible ? undefined : { color: "#52c41a" }}
              icon={<PoweroffOutlined />}
              onClick={async () => {
                const result = await saveProfile(row.profileId, {
                  payrollEligible: !row.payrollEligible,
                });
                if (result) refresh(filters);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Remove this employee from payroll setup?"
            description="The staff user will not be deleted."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={async () => {
              const removed = await deleteProfile(row.profileId);
              if (removed) refresh(filters);
            }}
          >
            <Tooltip title="Delete payroll profile">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ].filter(Boolean);

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <div>
        <Title level={3} style={{ marginBottom: 4 }}>
          Payroll Setup
        </Title>
        <Text type="secondary">Prepare employee records.</Text>
      </div>
      <Tabs
        items={[
          {
            key: "employees",
            label: "Employees",
            children: (
              <Space
                direction="vertical"
                size="large"
                style={{ width: "100%" }}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={12} lg={6}>
                    <Card>
                      <Statistic
                        title="Employees"
                        value={summary.totalProfiles}
                        prefix={<TeamOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} lg={6}>
                    <Card>
                      <Statistic
                        title="Ready"
                        value={summary.readyProfiles}
                        valueStyle={{ color: "#389e0d" }}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} lg={6}>
                    <Card>
                      <Statistic
                        title="Incomplete"
                        value={summary.incompleteProfiles}
                        valueStyle={{ color: "#d48806" }}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} lg={6}>
                    <Card>
                      <Statistic
                        title="Payroll eligible"
                        value={summary.eligibleProfiles}
                      />
                    </Card>
                  </Col>
                </Row>

                <Card>
                  <Row gutter={[12, 12]} justify="space-between">
                    <Col xs={24} xl={14} style={{ minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 12,
                          width: "100%",
                        }}
                      >
                        <Input.Search
                          allowClear
                          placeholder="Search employee, staff ID or job title"
                          style={{ width: 290, maxWidth: "100%" }}
                          onChange={(event) =>
                            setFilters((old) => ({
                              ...old,
                              search: event.target.value,
                            }))
                          }
                        />
                        <Select
                          allowClear
                          placeholder="Department"
                          style={{ width: 190 }}
                          options={departments.map((item) => ({
                            value: item.departmentId,
                            label: item.name,
                          }))}
                          onChange={(departmentId) =>
                            setFilters((old) => ({ ...old, departmentId }))
                          }
                        />
                        <Select
                          allowClear
                          placeholder="Readiness"
                          style={{ width: 150 }}
                          options={[
                            { value: "ready", label: "Ready" },
                            { value: "incomplete", label: "Incomplete" },
                          ]}
                          onChange={(readiness) =>
                            setFilters((old) => ({ ...old, readiness }))
                          }
                        />
                      </div>
                    </Col>
                    <Col xs={24} xl={10} style={{ textAlign: "right" }}>
                      <Space wrap>
                        <PayrollProfileBulkActions />
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={() => openProfile()}
                        >
                          Add employee
                        </Button>
                      </Space>
                    </Col>
                  </Row>
                </Card>

                <Card bodyStyle={{ padding: 0 }}>
                  <Table
                    rowKey="profileId"
                    columns={columns}
                    dataSource={profiles}
                    loading={loading}
                    pagination={{ pageSize: 10, showSizeChanger: true }}
                    scroll={{ x: 900 }}
                  />
                </Card>
              </Space>
            ),
          },
          {
            key: "components",
            label: "Components",
            children: (
              <PayrollComponentsTab
                components={components}
                saving={saving}
                saveComponent={saveComponent}
                deleteComponent={deleteComponent}
                copyVersion={(id, data) =>
                  copySetupVersion("component", id, data)
                }
              />
            ),
          },
          {
            key: "structures",
            label: "Salary Structures",
            children: (
              <PayrollStructuresTab
                components={components}
                structures={structures}
                saving={saving}
                saveStructure={saveStructure}
                deleteStructure={deleteStructure}
                copyVersion={(id, data) =>
                  copySetupVersion("structure", id, data)
                }
              />
            ),
          },
          {
            key: "scales",
            label: "Salary Scales",
            children: (
              <PayrollSalaryScalesTab
                scales={scales}
                saving={saving}
                saveScale={saveScale}
                deleteScale={deleteScale}
                copyVersion={(id, data) => copySetupVersion("scale", id, data)}
              />
            ),
          },
          payrollAccess.bank && {
            key: "banks",
            label: "Banks & Payment Accounts",
            children: (
              <PayrollBanksTab
                banks={banks}
                paymentAccounts={paymentAccounts}
                saving={saving}
                saveBank={saveBank}
                deleteBank={deleteBank}
                savePaymentAccount={savePaymentAccount}
                deletePaymentAccount={deletePaymentAccount}
                setDefaultPaymentAccount={setDefaultPaymentAccount}
              />
            ),
          },
          payrollAccess.sacco && {
            key: "sacco",
            label: "SACCO",
            children: <PayrollSaccoTab profiles={profiles} />,
          },
          {
            key: "rules",
            label: "Payroll Rules",
            children: (
              <PayrollStatutoryRulesTab
                rules={statutoryRules}
                saving={saving}
                fetchUgandaDefaults={fetchUgandaStatutoryDefaults}
                saveRule={saveStatutoryRule}
                deleteRule={deleteStatutoryRule}
                copyVersion={(id, data) => copySetupVersion("rule", id, data)}
                ruleAction={runRuleAction}
                testDraftRule={testDraftStatutoryRule}
              />
            ),
          },
        ].filter(Boolean)}
      />

      <PayrollEmployeeOverridesModal
        employee={overrideEmployee}
        components={components}
        overrides={componentOverrides}
        saving={saving}
        onClose={() => setOverrideEmployee(null)}
        saveOverride={saveComponentOverride}
        deleteOverride={deleteComponentOverride}
      />

      <Modal
        title={
          selected ? `Edit ${selected.staffName}` : "Add employee to payroll"
        }
        open={profileOpen}
        onCancel={() => setProfileOpen(false)}
        onOk={submitProfile}
        confirmLoading={saving}
        width={900}
        okText="Save employee"
      >
        <Form form={profileForm} layout="vertical">
          <Title level={5}>Employment</Title>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="staffId"
                label="Staff member"
                rules={[{ required: true }]}
              >
                <Select
                  showSearch
                  disabled={Boolean(selected)}
                  optionFilterProp="label"
                  options={staff.map((item) => ({
                    value: item.userID,
                    label: `${item.fullName || item.email}${item.staffId ? ` (${item.staffId})` : ""}`,
                    disabled:
                      usedStaffIds.has(item.userID) &&
                      item.userID !== selected?.staffId,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="departmentId"
                label="Department"
                rules={[{ required: true }]}
              >
                <Select
                  options={departments.map((item) => ({
                    value: item.departmentId,
                    label: item.name,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="schoolSection"
                label="School section"
                rules={[{ required: true }]}
              >
                <Select
                  options={getPayrollSchoolSectionOptions(profileSchoolSection)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="costCentre" label="Cost centre">
                <Input placeholder="For example: Secondary Teaching" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="designation"
                label="Job title"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="employeeCategory"
                label="Employee category"
                rules={[{ required: true }]}
              >
                <Select
                  options={["Teaching", "Support", "Administration"].map(
                    (value) => ({ value, label: value }),
                  )}
                  onChange={() => {
                    if (!selected) {
                      profileForm.setFieldsValue({
                        initialSalaryStructureId: null,
                        initialSalaryScaleId: null,
                        initialSalaryScaleStepId: null,
                        initialBasicSalary: null,
                        initialSalaryOverrideReason: null,
                      });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="employmentStatus"
                label="Employment status"
                rules={[{ required: true }]}
              >
                <Select
                  options={["Active", "On Leave", "Suspended", "Exited"].map(
                    (value) => ({ value, label: value }),
                  )}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="hireDate" label="Hire date">
                <DatePicker
                  style={{ width: "100%" }}
                  onChange={(value) => {
                    const exitDate = profileForm.getFieldValue("exitDate");
                    if (value && exitDate && exitDate.isBefore(value, "day")) {
                      profileForm.setFieldValue("exitDate", null);
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                name="exitDate"
                label="Exit date"
                dependencies={["hireDate"]}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const start = getFieldValue("hireDate");
                      if (!value || !start || !value.isBefore(start, "day"))
                        return Promise.resolve();
                      return Promise.reject(
                        new Error("Exit date cannot be before the hire date."),
                      );
                    },
                  }),
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  disabledDate={(current) =>
                    Boolean(hireDate && current?.isBefore(hireDate, "day"))
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="payrollEligible"
                label="Include in payroll"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          {!selected && payrollAccess.salary_full && (
            <>
              <Title level={5}>Initial salary</Title>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="initialSalaryStructureId"
                    label="Salary structure"
                  >
                    <Select
                      showSearch
                      allowClear
                      optionFilterProp="label"
                      options={activeStructuresFor(employeeCategory).map(
                        (item) => ({
                          value: item.structureId,
                          label: `${item.name} (${item.code})`,
                        }),
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="initialSalaryScaleId" label="Salary scale">
                    <Select
                      showSearch
                      allowClear
                      optionFilterProp="label"
                      options={activeScalesFor(employeeCategory).map(
                        (item) => ({
                          value: item.scaleId,
                          label: `${item.name} (Step code: ${item.stepCode || item.code})`,
                        }),
                      )}
                      onChange={() =>
                        profileForm.setFieldsValue({
                          initialSalaryScaleStepId: null,
                          initialBasicSalary: null,
                          initialSalaryOverrideReason: null,
                        })
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="initialSalaryScaleStepId"
                    label="Salary step"
                  >
                    <Select
                      disabled={!initialScale}
                      allowClear
                      options={
                        initialScale?.steps
                          ?.filter((item) => item.isActive)
                          .map((item) => ({
                            value: item.stepId,
                            label: `${item.name} (${money(item.basicSalary)})`,
                          })) || []
                      }
                      onChange={(stepId) => {
                        const step = initialScale?.steps?.find(
                          (item) => item.stepId === stepId,
                        );
                        profileForm.setFieldsValue({
                          initialBasicSalary: step
                            ? Number(step.basicSalary)
                            : null,
                          initialSalaryOverrideReason: null,
                        });
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="initialBasicSalary"
                    label="Monthly basic salary (UGX)"
                    rules={[{ required: true }]}
                  >
                    <InputNumber
                      {...payrollAmountInputProps}
                      min={1}
                      precision={2}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="initialSalaryEffectiveFrom"
                    label="Effective from"
                    rules={[{ required: true }]}
                  >
                    <DatePicker style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                {initialSalaryOverride && (
                  <Col span={24}>
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message={`This differs from the selected step amount of ${money(initialStep.basicSalary)}.`}
                    />
                    <Form.Item
                      name="initialSalaryOverrideReason"
                      label="Reason for salary override"
                      rules={[
                        {
                          required: true,
                          message:
                            "Explain why this salary differs from the scale step.",
                        },
                      ]}
                    >
                      <Input.TextArea rows={2} />
                    </Form.Item>
                  </Col>
                )}
              </Row>

              {payrollAccess.allowances_manage && (
                <>
                  <Title level={5}>Employee earnings & deductions</Title>
                  <Form.List name="initialAllowances">
                    {(fields, { add, remove }) => (
                      <Space direction="vertical" style={{ width: "100%" }}>
                        {fields.map((field, index) => {
                          const employeeValue = initialAllowances[index] || {};
                          const component = employeeSetupComponents.find(
                            (item) =>
                              item.componentId === employeeValue.componentId,
                          );
                          const usePercentage =
                            component?.calculationMethod?.startsWith(
                              "Percentage",
                            );
                          const selectedComponentIds = new Set(
                            initialAllowances
                              .map((item) => item.componentId)
                              .filter(Boolean),
                          );
                          return (
                            <Row gutter={12} align="bottom" key={field.key}>
                              <Col xs={24} md={8}>
                                <Form.Item
                                  name={[field.name, "componentId"]}
                                  label="Allowance or deduction"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Select a component",
                                    },
                                  ]}
                                >
                                  <Select
                                    showSearch
                                    allowClear
                                    optionFilterProp="label"
                                    options={employeeSetupComponents
                                      .filter(
                                        (item) =>
                                          item.componentId ===
                                            employeeValue.componentId ||
                                          !selectedComponentIds.has(
                                            item.componentId,
                                          ),
                                      )
                                      .map((item) => ({
                                        value: item.componentId,
                                        label: `${item.name} — ${item.kind}`,
                                      }))}
                                    onChange={(componentId) => {
                                      const selectedComponent =
                                        employeeSetupComponents.find(
                                          (item) =>
                                            item.componentId === componentId,
                                        );
                                      const isArrears =
                                        selectedComponent?.code === "ARREARS";
                                      const componentDefaults =
                                        getPayrollComponentDefaults(
                                          selectedComponent,
                                        );
                                      profileForm.setFieldValue(
                                        ["initialAllowances", index, "amount"],
                                        componentDefaults.amount,
                                      );
                                      profileForm.setFieldValue(
                                        [
                                          "initialAllowances",
                                          index,
                                          "percentage",
                                        ],
                                        componentDefaults.percentage,
                                      );
                                      profileForm.setFieldValue(
                                        [
                                          "initialAllowances",
                                          index,
                                          "effectiveTo",
                                        ],
                                        isArrears
                                          ? (
                                              profileForm.getFieldValue(
                                                "initialSalaryEffectiveFrom",
                                              ) || dayjs()
                                            ).endOf("month")
                                          : null,
                                      );
                                    }}
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={20} md={6}>
                                <Form.Item
                                  name={[
                                    field.name,
                                    usePercentage ? "percentage" : "amount",
                                  ]}
                                  label={
                                    usePercentage
                                      ? "Percentage"
                                      : "Amount (UGX)"
                                  }
                                  rules={[
                                    {
                                      required: true,
                                      message: usePercentage
                                        ? "Enter percentage"
                                        : "Enter amount",
                                    },
                                  ]}
                                  extra="The configured default is prefilled and can be changed for this employee."
                                >
                                  <InputNumber
                                    {...(!usePercentage
                                      ? payrollAmountInputProps
                                      : {})}
                                    min={0}
                                    max={usePercentage ? 100 : undefined}
                                    precision={usePercentage ? 4 : 2}
                                    addonAfter={usePercentage ? "%" : undefined}
                                    style={{ width: "100%" }}
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={20} md={7}>
                                <Form.Item
                                  name={[field.name, "effectiveTo"]}
                                  label="Effective to"
                                >
                                  <DatePicker style={{ width: "100%" }} />
                                </Form.Item>
                              </Col>
                              <Col xs={4} md={3}>
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => remove(field.name)}
                                  aria-label="Remove employee earning or deduction"
                                />
                              </Col>
                            </Row>
                          );
                        })}
                        <Space direction="vertical" size={4}>
                          <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={() => add({})}
                            disabled={!employeeSetupComponents.length}
                          >
                            Add allowance / deduction
                          </Button>
                          {!employeeSetupComponents.length && (
                            <Text type="secondary">
                              Create an active earning or employee deduction
                              with employee-specific values enabled in Payroll
                              Setup.
                            </Text>
                          )}
                        </Space>
                      </Space>
                    )}
                  </Form.List>
                </>
              )}
            </>
          )}

          {selected && payrollAccess.allowances_manage && (
            <>
              <Title level={5}>Employee earnings & deductions</Title>
              <PayrollEmployeeOverridesModal
                inline
                employee={selected}
                components={components}
                overrides={componentOverrides}
                saving={saving}
                saveOverride={saveComponentOverride}
                deleteOverride={deleteComponentOverride}
              />
            </>
          )}

          {(payrollAccess.tax || payrollAccess.nssf) && (
            <>
              <Title level={5}>Tax, NSSF and LST details</Title>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item name="residencyStatus" label="Tax residency">
                    <Select
                      options={["Resident", "Non-Resident"].map((value) => ({
                        value,
                        label: value,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="tin" label="TIN">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item
                    name="taxExempt"
                    label="Tax exempt"
                    valuePropName="checked"
                  >
                    <Switch
                      onChange={(checked) => {
                        if (!checked)
                          profileForm.setFieldValue("taxExemptReason", null);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item
                    name="nssfEnabled"
                    label="NSSF enabled"
                    valuePropName="checked"
                  >
                    <Switch
                      onChange={(checked) => {
                        if (!checked)
                          profileForm.setFieldValue("nssfNumber", null);
                      }}
                    />
                  </Form.Item>
                </Col>
                {taxExempt && (
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="taxExemptReason"
                      label="Tax exemption reason"
                      rules={[
                        {
                          required: true,
                          message:
                            "Enter the reason this employee is tax exempt.",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                )}
                {nssfEnabled && (
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="nssfNumber"
                      label="NSSF number"
                      rules={[
                        {
                          required: true,
                          message: "Enter the employee NSSF number.",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                )}
                <Col xs={12} md={6}>
                  <Form.Item
                    name="lstEnabled"
                    label="LST enabled"
                    valuePropName="checked"
                  >
                    <Switch
                      onChange={(checked) => {
                        if (!checked)
                          profileForm.setFieldValue("lstLocation", null);
                      }}
                    />
                  </Form.Item>
                </Col>
                {lstEnabled && (
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="lstLocation"
                      label="LST assessment location (optional)"
                    >
                      <Input placeholder="For example: Kira Municipal Council" />
                    </Form.Item>
                  </Col>
                )}
                {selected && (
                  <>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="lstEffectiveFrom"
                        label="LST change takes effect"
                        dependencies={["lstEnabled", "lstLocation"]}
                        rules={[
                          { required: true },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (
                                lstEffectiveFloor &&
                                value &&
                                lstDetailsChanged(getFieldValue, selected) &&
                                !dayjs(value).isAfter(lstEffectiveFloor, "day")
                              ) {
                                return Promise.reject(
                                  new Error(
                                    "Choose a date after the current LST effective date, or the change will overlap the current version.",
                                  ),
                                );
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <DatePicker
                          style={{ width: "100%" }}
                          disabledDate={(current) =>
                            Boolean(
                              lstEffectiveFloor &&
                              current &&
                              !current.isAfter(lstEffectiveFloor, "day"),
                            )
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="lstChangeReason"
                        label="Reason if LST details changed"
                      >
                        <Input placeholder="Required when LST details change" />
                      </Form.Item>
                    </Col>
                  </>
                )}
              </Row>
            </>
          )}

          {payrollAccess.sacco && (
            <>
              <Title level={5}>SACCO member mapping</Title>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item name="saccoAccountNo" label="SACCO account number">
                    <Input
                      autoComplete="off"
                      placeholder="Leave blank if the employee is not a SACCO member"
                    />
                  </Form.Item>
                </Col>
                {selected?.saccoAccountNo && (
                  <Col xs={24} md={12}>
                    <Text type="secondary">Verification</Text>
                    <div>
                      <Tag
                        color={
                          selected.saccoVerified
                            ? "green"
                            : selected.saccoVerificationError
                              ? "red"
                              : "default"
                        }
                      >
                        {selected.saccoVerified ? "Verified" : "Not verified"}
                      </Tag>
                    </div>
                  </Col>
                )}
              </Row>
            </>
          )}

          {payrollAccess.bank && (
            <>
              <Title level={5}>Employee Bank Details</Title>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="bankId"
                    label="Bank"
                    rules={[{ required: true }]}
                  >
                    <Select
                      showSearch
                      optionFilterProp="label"
                      options={banks
                        .filter((item) => item.isActive)
                        .map((item) => ({
                          value: item.bankId,
                          label: item.code
                            ? `${item.name} (${item.code})`
                            : item.name,
                        }))}
                      onChange={() =>
                        profileForm.setFieldsValue({ bankBranchCode: null })
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="bankBranch" label="Bank branch">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="bankBranchCode"
                    label="Branch code"
                    rules={
                      selectedBankMaster?.requiresBranchCode
                        ? [
                            {
                              required: true,
                              message: "This bank requires a branch code.",
                            },
                          ]
                        : []
                    }
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="bankAccountName"
                    label="Account name"
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="bankAccountNumber"
                    label={
                      selected
                        ? `Account number (${selected.maskedBankAccount || "not set"})`
                        : "Account number"
                    }
                    rules={selected ? [] : [{ required: true }]}
                  >
                    <Input
                      placeholder={
                        selected ? "Leave blank to keep the current number" : ""
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="bankEffectiveFrom"
                    label="Bank details effective from"
                    dependencies={[
                      "bankId",
                      "bankBranch",
                      "bankBranchCode",
                      "bankAccountName",
                      "bankAccountNumber",
                    ]}
                    rules={[
                      { required: true },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (
                            bankEffectiveFloor &&
                            value &&
                            bankDetailsChanged(getFieldValue, selected) &&
                            !dayjs(value).isAfter(bankEffectiveFloor, "day")
                          ) {
                            return Promise.reject(
                              new Error(
                                "Choose a date after the current bank effective date, or the change will overlap the current version.",
                              ),
                            );
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      disabledDate={(current) =>
                        Boolean(
                          bankEffectiveFloor &&
                          current &&
                          !current.isAfter(bankEffectiveFloor, "day"),
                        )
                      }
                    />
                  </Form.Item>
                </Col>
                {selected && (
                  <Col span={24}>
                    <Form.Item
                      name="bankChangeReason"
                      label="Reason for changing bank details"
                      extra="Required only when the bank, branch, account name, or account number changes."
                    >
                      <Input.TextArea rows={2} />
                    </Form.Item>
                  </Col>
                )}
                <Col span={24}>
                  <Form.Item name="notes" label="Internal notes">
                    <Input.TextArea rows={2} />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Modal>

      <Modal
        title={`Salary history — ${salaryHistoryEmployee?.staffName || ""}`}
        open={Boolean(salaryHistoryEmployee)}
        onCancel={() => {
          setSalaryHistoryEmployee(null);
          setSalaryHistory([]);
        }}
        width={1100}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setSalaryHistoryEmployee(null);
              setSalaryHistory([]);
            }}
          >
            Close
          </Button>,
          <Button
            key="revision"
            type="primary"
            onClick={() =>
              openSalary({
                ...salaryHistoryEmployee,
                currentSalary: currentHistorySalary,
              })
            }
          >
            {currentHistorySalary
              ? "Create salary revision"
              : "Set initial salary"}
          </Button>,
        ]}
      >
        <Table
          rowKey="assignmentId"
          dataSource={salaryHistory}
          loading={salaryHistoryLoading}
          pagination={false}
          scroll={{ x: 900 }}
          columns={[
            {
              title: "Salary",
              render: (_, row) => (
                <Space direction="vertical" size={0}>
                  <Text strong>{money(row.basicSalary)}</Text>
                  <Text type="secondary">
                    {row.salaryStructureName || "No salary structure"}
                  </Text>
                  <Text type="secondary">
                    {row.salaryScaleName
                      ? `${row.salaryScaleName} · ${row.salaryScaleStepName}`
                      : "No salary scale"}
                  </Text>
                </Space>
              ),
            },
            {
              title: "Effective period",
              render: (_, row) => (
                <Space direction="vertical" size={0}>
                  <Text>{dayjs(row.effectiveFrom).format("DD MMM YYYY")}</Text>
                  <Text type="secondary">
                    to{" "}
                    {row.effectiveTo
                      ? dayjs(row.effectiveTo).format("DD MMM YYYY")
                      : "Current/Open"}
                  </Text>
                </Space>
              ),
            },
            {
              title: "Payroll usage",
              render: (_, row) =>
                row.payrollUsageCount ? (
                  <Space direction="vertical" size={2}>
                    {row.payrollUsage.slice(0, 3).map((usage) => (
                      <Tag
                        key={`${usage.runId}-${usage.periodName}`}
                        color={usage.runStatus === "Draft" ? "gold" : "blue"}
                      >
                        {usage.periodName} · {usage.runStatus}
                      </Tag>
                    ))}
                    {row.payrollUsageCount > 3 && (
                      <Text type="secondary">
                        +{row.payrollUsageCount - 3} more
                      </Text>
                    )}
                  </Space>
                ) : (
                  <Tag>Not used by payroll</Tag>
                ),
            },
            {
              title: "Reason / notes",
              render: (_, row) => (
                <Space direction="vertical" size={0}>
                  <Text>{row.revisionReason || "—"}</Text>
                  {row.notes && <Text type="secondary">{row.notes}</Text>}
                </Space>
              ),
            },
            {
              title: "Status",
              render: (_, row) => (
                <Space direction="vertical" size={2}>
                  {row.isCurrent && <Tag color="green">Current</Tag>}
                  {row.hasProtectedUsage ? (
                    <Tag color="blue" icon={<LockOutlined />}>
                      Protected history
                    </Tag>
                  ) : row.isUsedByPayroll ? (
                    <Tag color="gold" icon={<ReloadOutlined />}>
                      Draft usage — regenerate after editing
                    </Tag>
                  ) : (
                    <Tag color="orange">Unused</Tag>
                  )}
                </Space>
              ),
            },
            {
              title: "Actions",
              width: 120,
              align: "center",
              render: (_, row) =>
                row.canCorrect || row.canDelete ? (
                  <Space size="small">
                    {row.canCorrect && (
                      <Tooltip title="Correct effective dates">
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => openSalaryCorrection(row)}
                        />
                      </Tooltip>
                    )}
                    {row.canDelete && (
                      <Popconfirm
                        title="Delete this unused salary record?"
                        description="Use this only for an erroneous or duplicate setup record."
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => removeSalaryRecord(row)}
                      >
                        <Tooltip title="Delete unused record">
                          <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                          />
                        </Tooltip>
                      </Popconfirm>
                    )}
                  </Space>
                ) : (
                  <Tooltip title="This record is locked because a submitted or approved payroll uses it.">
                    <Button
                      type="text"
                      size="small"
                      disabled
                      icon={<LockOutlined />}
                    />
                  </Tooltip>
                ),
            },
          ]}
        />
      </Modal>

      <Modal
        title={`Correct salary dates — ${salaryHistoryEmployee?.staffName || ""}`}
        open={Boolean(salaryCorrection)}
        onCancel={() => setSalaryCorrection(null)}
        onOk={submitSalaryCorrection}
        confirmLoading={saving}
        okText="Save correction"
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Unused dates can be corrected safely"
          description="This record becomes authoritative for the dates you enter. Overlapping unused records are trimmed or removed automatically. Dates already used by payroll remain protected. Use a salary revision for an actual salary change."
        />
        <Space direction="vertical" size={0} style={{ marginBottom: 16 }}>
          <Text strong>{money(salaryCorrection?.basicSalary)}</Text>
          <Text type="secondary">
            {salaryCorrection?.salaryStructureName || "No salary structure"}
            {salaryCorrection?.salaryScaleStepName
              ? ` · ${salaryCorrection.salaryScaleStepName}`
              : ""}
          </Text>
        </Space>
        <Form form={salaryCorrectionForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="effectiveFrom"
                label="Effective from"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="effectiveTo"
                label="Effective to"
                dependencies={["effectiveFrom"]}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const start = getFieldValue("effectiveFrom");
                      if (!value || !start || !value.isBefore(start, "day"))
                        return Promise.resolve();
                      return Promise.reject(
                        new Error(
                          "Effective-to date cannot be before effective-from date.",
                        ),
                      );
                    },
                  }),
                ]}
              >
                <DatePicker
                  allowClear
                  disabledDate={(current) =>
                    Boolean(
                      salaryCorrectionStart &&
                      current?.isBefore(salaryCorrectionStart, "day"),
                    )
                  }
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="correctionReason"
            label="Reason for correcting this record"
            rules={[
              {
                required: true,
                message: "Explain why these dates are being corrected.",
              },
            ]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Bank-detail history — ${bankHistoryEmployee?.staffName || ""}`}
        open={Boolean(bankHistoryEmployee)}
        onCancel={() => setBankHistoryEmployee(null)}
        footer={null}
        width={900}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Account numbers stay masked. Each row shows when the details applied and why they changed."
          description="Payroll uses the bank-detail version effective during the payroll period. After correcting a branch code or bank requirement, regenerate any Draft payroll; approved payroll snapshots are not rewritten."
        />
        <Table
          rowKey="historyId"
          dataSource={bankHistory}
          pagination={false}
          columns={[
            {
              title: "Effective from",
              dataIndex: "effectiveFrom",
              render: (value) => dayjs(value).format("DD MMM YYYY"),
            },
            {
              title: "Effective to",
              dataIndex: "effectiveTo",
              render: (value) =>
                value ? dayjs(value).format("DD MMM YYYY") : "Current/Open",
            },
            {
              title: "Bank",
              render: (_, row) =>
                row.bankCode
                  ? `${row.bankName} (${row.bankCode})`
                  : row.bankName,
            },
            {
              title: "Account",
              render: (_, row) =>
                `${row.accountName} · ${row.maskedAccountNumber}`,
            },
            { title: "Reason", dataIndex: "changeReason" },
            { title: "Changed by", dataIndex: "changedByName" },
          ]}
        />
      </Modal>

      <Modal
        title={`${selected?.currentSalary ? "Revise" : "Set"} basic salary — ${selected?.staffName || ""}`}
        open={salaryOpen}
        onCancel={() => setSalaryOpen(false)}
        onOk={submitSalary}
        confirmLoading={saving}
        okText={selected?.currentSalary ? "Create revision" : "Save salary"}
      >
        {selected?.currentSalary && (
          <Alert
            style={{ marginBottom: 16 }}
            type="info"
            message="This creates a new salary record. The current salary will end the day before the new effective date and remain in history."
          />
        )}
        <Form form={salaryForm} layout="vertical">
          <Form.Item name="salaryStructureId" label="Salary structure">
            <Select
              allowClear
              optionFilterProp="label"
              options={activeStructuresFor(selected?.employeeCategory).map(
                (item) => ({
                  value: item.structureId,
                  label: `${item.name} (${item.code})`,
                }),
              )}
            />
          </Form.Item>
          <Form.Item name="salaryScaleId" label="Salary scale">
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              options={activeScalesFor(selected?.employeeCategory).map(
                (item) => ({
                  value: item.scaleId,
                  label: `${item.name} (Step code: ${item.stepCode || item.code})`,
                }),
              )}
              onChange={() =>
                salaryForm.setFieldsValue({
                  salaryScaleStepId: null,
                  basicSalary: null,
                  salaryOverrideReason: null,
                })
              }
            />
          </Form.Item>
          <Form.Item name="salaryScaleStepId" label="Salary step">
            <Select
              disabled={!salaryScale}
              allowClear
              options={
                salaryScale?.steps
                  ?.filter((item) => item.isActive)
                  .map((item) => ({
                    value: item.stepId,
                    label: `${item.name} (${money(item.basicSalary)})`,
                  })) || []
              }
              onChange={(stepId) => {
                const step = salaryScale?.steps?.find(
                  (item) => item.stepId === stepId,
                );
                salaryForm.setFieldsValue({
                  basicSalary: step ? Number(step.basicSalary) : null,
                  salaryOverrideReason: null,
                });
              }}
            />
          </Form.Item>
          <Form.Item
            name="basicSalary"
            label="Monthly basic salary (UGX)"
            rules={[{ required: true }]}
          >
            <InputNumber
              {...payrollAmountInputProps}
              min={1}
              precision={2}
              style={{ width: "100%" }}
            />
          </Form.Item>
          {salaryOverride && (
            <>
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message={`This differs from the selected step amount of ${money(salaryStep.basicSalary)}.`}
              />
              <Form.Item
                name="salaryOverrideReason"
                label="Reason for salary override"
                rules={[
                  {
                    required: true,
                    message:
                      "Explain why this salary differs from the scale step.",
                  },
                ]}
              >
                <Input.TextArea rows={2} />
              </Form.Item>
            </>
          )}
          <Form.Item
            name="effectiveFrom"
            label="Effective from"
            rules={[{ required: true }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          {selected?.currentSalary && (
            <Form.Item
              name="revisionReason"
              label="Reason for salary revision"
              rules={[
                {
                  required: true,
                  message: "Explain why the salary is changing.",
                },
              ]}
            >
              <Input.TextArea rows={2} />
            </Form.Item>
          )}
          <Form.Item name="paymentFrequency" label="Payment frequency">
            <Select
              disabled
              options={[{ value: "Monthly", label: "Monthly" }]}
            />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default PayrollManagement;
